import {
  CATEGORY_LAYER_IDS,
  MAP_LAB_BAND_COLORS,
  MAP_LAB_CLUSTERED_DOT_LAYER_IDS,
  MAP_LAB_DYNAMIC_SOURCE_ID,
  MAP_LAB_MIN_OPACITY,
  MAP_LAB_MIN_POPULATION,
  MAP_LAB_NODATA_COLOR,
  MAP_LAB_NON_ZIP_HATCH_IMAGE,
  MAP_LAB_NEIGHBOR_ZIP_LAYER_ID,
  MAP_LAB_NEIGHBOR_ZIP_LINE_COLOR,
  MAP_LAB_NEIGHBOR_ZIP_LINE_WIDTH,
  MAP_LAB_NEIGHBOR_ZIP_SOURCE_ID,
  MAP_LAB_NON_ZIP_LAYER_ID,
  MAP_LAB_OUTSIDE_MASK_COLOR,
  MAP_LAB_OUTSIDE_MASK_LAYER_ID,
  MAP_LAB_OUTSIDE_MASK_OPACITY,
  MAP_LAB_OUTSIDE_MASK_SOURCE_ID,
  MAP_LAB_PIE_LAYER_ID,
  MAP_LAB_STATE_FILL_COLOR,
  MAP_LAB_STATE_FILL_LAYER_ID,
  MAP_LAB_STATE_FILL_SOURCE_ID,
  MAP_LAB_POINT_COLORS,
  MAP_LAB_SHADE_MAX_OPACITY,
  MAP_LAB_SHADE_MIN_OPACITY,
  MAP_LAB_ZIP_SHADING_LAYER_IDS,
  mapInstances,
} from "./constants.js";
import {
  joinCountyCounts,
  joinZipCounts,
  mapLabCategoryGeoJson,
  mapLabColorMode,
  mapLabDotsVisible,
  mapLabDrillCounty,
  mapLabMagnitudeMetric,
  mapLabVisibility,
} from "./data.js";
import { ensurePieImages, outbreakPieFeature, pieDotCount, sickDotImageId } from "./pie.js";
import {
  emptyFeatureCollection,
  ensureNonZipHatchImage,
  loadArizonaCountyGeometry,
  loadArizonaNonZipGeometry,
  loadArizonaZipGeometry,
  loadNeighborZipGeometry,
  outsideArizonaMaskRectFeatureCollection,
  zipCentroidGeoJson,
} from "./geo.js";
import {
  buildCategoryClusterIndexes,
  buildClusteredFeatureCollectionForZoom,
} from "./clustering.js";
import { sickRateCssColor } from "./shading.js";

const FEATURE_KIND = {
  zip: "zip",
  county: "county",
  pie: "pie",
  person: "person",
  personCluster: "person-cluster",
  animal: "animal",
  animalCluster: "animal-cluster",
  environment: "environment",
  environmentCluster: "environment-cluster",
};

const POINT_CLUSTER_GRID_DEGREES = {
  person: 2.25,
  animals: 1.85,
  environment: 1.85,
};

export function ensureMapLabDynamicSource(map, sourceId = MAP_LAB_DYNAMIC_SOURCE_ID) {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: "geojson",
      data: emptyFeatureCollection(),
    });
  }
}


export async function buildMapLabDynamicFeatureCollection() {
  const [zipGeometry, countyGeometry] = await Promise.all([
    loadArizonaZipGeometry(),
    loadArizonaCountyGeometry(),
  ]);
  const zipCounts = joinZipCounts(zipGeometry);
  const countyCounts = joinCountyCounts(countyGeometry);
  const zipPropertiesByZip = new Map(
    (zipCounts.features || []).map((feature) => [
      feature.properties?.GEOID || feature.properties?.ZCTA5 || "",
      feature.properties || {},
    ])
  );

  const zipFeatures = (zipCounts.features || []).map((feature) => polygonFeature(feature, FEATURE_KIND.zip));
  const countyFeatures = (countyCounts.features || []).map((feature) => polygonFeature(feature, FEATURE_KIND.county));
  const personFeatures = (zipCentroidGeoJson(zipGeometry).features || []).map((feature) => {
    const zipProps = zipPropertiesByZip.get(feature.properties?.zip_code) || {};
    return personFeature(feature, zipProps);
  });
  const animalFeatures = categoryPointFeatures("animals", FEATURE_KIND.animal);
  const environmentFeatures = categoryPointFeatures("environment", FEATURE_KIND.environment);

  const personClusterFeatures = clusterPointFeatures(personFeatures, FEATURE_KIND.personCluster, {
    category: "person",
    cellSize: POINT_CLUSTER_GRID_DEGREES.person,
  });
  const animalClusterFeatures = clusterPointFeatures(animalFeatures, FEATURE_KIND.animalCluster, {
    category: "animals",
    cellSize: POINT_CLUSTER_GRID_DEGREES.animals,
  });
  const environmentClusterFeatures = clusterPointFeatures(environmentFeatures, FEATURE_KIND.environmentCluster, {
    category: "environment",
    cellSize: POINT_CLUSTER_GRID_DEGREES.environment,
  });

  return {
    type: "FeatureCollection",
    features: [
      ...zipFeatures,
      ...countyFeatures,
      ...personClusterFeatures,
      ...animalClusterFeatures,
      ...environmentClusterFeatures,
      ...personFeatures,
      ...animalFeatures,
      ...environmentFeatures,
    ],
  };
}


export async function buildMapLabBaseData(options = {}) {
  const baseGeneration = options.baseGeneration ?? 0;
  const [zipGeometry, countyGeometry] = await Promise.all([
    loadArizonaZipGeometry(),
    loadArizonaCountyGeometry(),
  ]);
  const zipCounts = joinZipCounts(zipGeometry);
  const countyCounts = joinCountyCounts(countyGeometry);
  const zipPropertiesByZip = new Map(
    (zipCounts.features || []).map((feature) => [
      feature.properties?.GEOID || feature.properties?.ZCTA5 || "",
      feature.properties || {},
    ])
  );

  const zipFeatures = (zipCounts.features || []).map((feature) => polygonFeature(feature, FEATURE_KIND.zip));
  const countyFeatures = (countyCounts.features || []).map((feature) => polygonFeature(feature, FEATURE_KIND.county));
  const zipCentroids = zipCentroidGeoJson(zipGeometry).features || [];
  const peoplePointFeatures = zipCentroids.map((feature) => {
    const zipProps = zipPropertiesByZip.get(feature.properties?.zip_code) || {};
    return personFeature(feature, zipProps);
  });
  // Outbreak dots are independent of zoom/clustering. Count properties are merged before
  // filtering so ZIPs with no sick reports drop out at the source boundary.
  const pieFeatures = peoplePieFeatures(zipCentroids, zipPropertiesByZip);
  const animalPointFeatures = categoryPointFeatures("animals", FEATURE_KIND.animal);
  const environmentPointFeatures = categoryPointFeatures("environment", FEATURE_KIND.environment);

  return {
    baseGeneration,
    zipFeatures,
    countyFeatures,
    pieFeatures,
    peoplePointFeatures,
    animalPointFeatures,
    environmentPointFeatures,
    clusterIndexes: buildCategoryClusterIndexes({
      people: peoplePointFeatures,
      animals: animalPointFeatures,
      environment: environmentPointFeatures,
    }, { baseGeneration }),
  };
}


export function buildMapLabFeatureCollectionForZoom(baseData, zoom) {
  return buildClusteredFeatureCollectionForZoom(baseData, zoom);
}


export function setMapLabDynamicSourceData(map, featureCollection, sourceId = MAP_LAB_DYNAMIC_SOURCE_ID) {
  const source = map.getSource(sourceId);
  if (!source) return 0;
  source.setData(featureCollection);
  return 1;
}


function polygonFeature(feature, kind) {
  const properties = feature.properties || {};
  const isZip = kind === FEATURE_KIND.zip;
  return {
    ...feature,
    properties: {
      ...properties,
      kind,
      label: isZip
        ? properties.GEOID || properties.ZCTA5 || ""
        : properties.county_name || properties.NAME || "",
      fillColor: polygonFillColor(properties),
      fillOpacity: polygonFillOpacity(properties),
      outlineColor: isZip ? "#52525b" : "#3f3f46",
      outlineOpacity: isZip ? 0.48 : 0.55,
    },
  };
}


function polygonFillColor(properties) {
  if (isSuppressedPolygon(properties)) return MAP_LAB_NODATA_COLOR;
  if (mapLabColorMode() === "banded") return bandColor(properties.sick_band);
  return sickRateCssColor(properties.sick_rate);
}


function polygonFillOpacity(properties) {
  const metric = mapLabMagnitudeMetric();
  if (metric === "none") return 0;
  if (isSuppressedPolygon(properties)) return MAP_LAB_MIN_OPACITY;
  if (metric === "flat") return MAP_LAB_SHADE_MAX_OPACITY;
  const normProperty = metric === "rate" ? "rate_norm" : "reports_norm";
  return interpolateUnit(Number(properties[normProperty]), MAP_LAB_SHADE_MIN_OPACITY, MAP_LAB_SHADE_MAX_OPACITY);
}


function isSuppressedPolygon(properties) {
  if (toNumber(properties.total_count) <= 0) return true;
  return mapLabMagnitudeMetric() === "rate" && toNumber(properties.population) < MAP_LAB_MIN_POPULATION;
}


function personFeature(feature, zipProperties) {
  const properties = { ...(feature.properties || {}), ...zipProperties };
  return {
    ...feature,
    properties: {
      ...properties,
      kind: FEATURE_KIND.person,
      category: "people",
      iconType: "person",
      dotColor: pointSickRateColor(properties),
      dotOpacity: 0.85,
      radius: interpolateStops(toNumber(properties.total_count), [
        [1, 5],
        [25, 11],
        [100, 17],
        [220, 24],
      ]),
    },
  };
}


function peoplePieFeatures(zipCentroids, zipPropertiesByZip) {
  const prepared = zipCentroids.map((feature) => {
    const zipProperties = zipPropertiesByZip.get(feature.properties?.zip_code) || {};
    return { ...feature, properties: { ...(feature.properties || {}), ...zipProperties } };
  });
  // Dots are sized against the typical drawn count (see dotDiameter), so the pool has to
  // be exactly the ZIPs that draw one — pieDotCount zeroes out the ones outbreakPieFeature
  // is about to drop, keeping them from dragging the median down.
  const sortedCounts = prepared
    .map((feature) => pieDotCount(feature.properties))
    .filter((count) => count > 0)
    .sort((a, b) => a - b);
  return prepared
    .map((feature) => outbreakPieFeature(feature, { kind: FEATURE_KIND.pie, sortedCounts }))
    .filter(Boolean);
}


function categoryPointFeatures(category, kind) {
  return (mapLabCategoryGeoJson(category).features || []).map((feature) => ({
    ...feature,
    properties: {
      ...(feature.properties || {}),
      kind,
      iconType: category,
      dotColor: MAP_LAB_POINT_COLORS[category],
      dotOpacity: 0.9,
      radius: 7,
    },
  }));
}


function clusterPointFeatures(features, clusterKind, options) {
  const buckets = new Map();
  features.forEach((feature) => {
    const coordinates = feature.geometry?.coordinates || [];
    const lng = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const key = [
      Math.floor((lng + 180) / options.cellSize),
      Math.floor((lat + 90) / options.cellSize),
    ].join(":");
    const bucket = buckets.get(key) || emptyClusterBucket();
    const weight = options.category === "person" ? Math.max(1, toNumber(feature.properties?.total_count)) : 1;
    bucket.lng += lng * weight;
    bucket.lat += lat * weight;
    bucket.weight += weight;
    bucket.pointCount += 1;
    bucket.sumTotal += toNumber(feature.properties?.total_count);
    bucket.sumSick += toNumber(feature.properties?.sick_count);
    bucket.features.push(feature);
    buckets.set(key, bucket);
  });

  return [...buckets.values()]
    .filter((bucket) => {
      bucket.features.forEach((feature) => {
        feature.properties.clusterSize = bucket.pointCount;
      });
      return bucket.pointCount >= 2;
    })
    .map((bucket) => clusterFeature(bucket, clusterKind, options.category));
}


function emptyClusterBucket() {
  return {
    lng: 0,
    lat: 0,
    weight: 0,
    pointCount: 0,
    sumTotal: 0,
    sumSick: 0,
    features: [],
  };
}


function clusterFeature(bucket, kind, category) {
  const isPerson = category === "person";
  const total = isPerson ? bucket.sumTotal : bucket.pointCount;
  const sickRate = bucket.sumTotal > 0 ? bucket.sumSick / bucket.sumTotal : 0;
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [bucket.lng / bucket.weight, bucket.lat / bucket.weight],
    },
    properties: {
      kind,
      category: isPerson ? "people" : category,
      point_count: bucket.pointCount,
      total_count: total,
      sick_count: bucket.sumSick,
      sum_total: bucket.sumTotal,
      sum_sick: bucket.sumSick,
      sick_rate: sickRate,
      clusterLabel: String(total),
      iconType: `${category}-cluster`,
      dotColor: isPerson ? sickRateCssColor(sickRate) : MAP_LAB_POINT_COLORS[category],
      dotOpacity: isPerson ? 0.9 : 0.9,
      radius: isPerson
        ? interpolateStops(total, [
          [10, 16],
          [200, 24],
          [1000, 34],
          [4000, 46],
        ])
        : interpolateStops(total, [
          [2, 20],
          [10, 25],
          [50, 32],
          [200, 40],
        ]),
    },
  };
}


function pointSickRateColor(properties) {
  if (mapLabColorMode() === "banded") return bandColor(properties.sick_band);
  return sickRateCssColor(properties.sick_rate);
}


function bandColor(value) {
  const band = Number(value);
  return MAP_LAB_BAND_COLORS[Math.min(2, Math.max(0, Number.isFinite(band) ? band : 1))] || MAP_LAB_BAND_COLORS[1];
}


function interpolateUnit(value, min, max) {
  const normalized = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  return min + (max - min) * normalized;
}


function interpolateStops(value, stops) {
  const x = Number(value);
  if (!Number.isFinite(x)) return stops[0][1];
  if (x <= stops[0][0]) return stops[0][1];
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [x0, y0] = stops[index];
    const [x1, y1] = stops[index + 1];
    if (x <= x1) {
      const t = (x - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }
  return stops[stops.length - 1][1];
}


function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}


function kindFilter(kind) {
  return ["==", ["get", "kind"], kind];
}


function singletonPointFilter(kind) {
  return ["all", kindFilter(kind), ["<", ["coalesce", ["get", "clusterSize"], 1], 2]];
}


function zipFilter() {
  const county = mapLabDrillCounty();
  const base = kindFilter(FEATURE_KIND.zip);
  return county ? ["all", base, ["==", ["get", "county"], county]] : base;
}


export function setLayerVisible(map, layerId, visible) {
  if (map.getLayer(layerId)) map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
}


// Spotlight mask dimming everything outside Arizona. Add this first among the app layers
// (right after the base map is flattened) so the fills render below the ZIP/county/dot
// layers but above the base map's symbol labels — dimming the surrounding-state labels
// while Arizona stays clear. Three stacked layers, no polygon holes: the neighboring
// states' ZIP outlines, then a gray rectangle over everything, then the county polygons
// repainted in the light base tone on top to restore the state. Order matters — the
// neighbor outlines go first precisely so the rectangle dims them too. See the layer note
// in constants.js for why holes are avoided.
// ZIP outlines for the neighboring states, so ZIP coverage visibly continues past the
// state line instead of stopping at it. Boundary lines only — there is no data behind
// them, so they get no fill and no interactions.
export async function addNeighborZipBoundaries(map) {
  if (!map.getSource(MAP_LAB_NEIGHBOR_ZIP_SOURCE_ID)) {
    map.addSource(MAP_LAB_NEIGHBOR_ZIP_SOURCE_ID, {
      type: "geojson",
      data: await loadNeighborZipGeometry(),
    });
  }
  if (!map.getLayer(MAP_LAB_NEIGHBOR_ZIP_LAYER_ID)) {
    map.addLayer({
      id: MAP_LAB_NEIGHBOR_ZIP_LAYER_ID,
      type: "line",
      source: MAP_LAB_NEIGHBOR_ZIP_SOURCE_ID,
      paint: {
        "line-color": MAP_LAB_NEIGHBOR_ZIP_LINE_COLOR,
        "line-width": MAP_LAB_NEIGHBOR_ZIP_LINE_WIDTH,
      },
    });
  }
}


export async function addOutsideArizonaMask(map) {
  await addNeighborZipBoundaries(map);

  if (!map.getSource(MAP_LAB_OUTSIDE_MASK_SOURCE_ID)) {
    map.addSource(MAP_LAB_OUTSIDE_MASK_SOURCE_ID, {
      type: "geojson",
      data: outsideArizonaMaskRectFeatureCollection(),
    });
  }
  if (!map.getLayer(MAP_LAB_OUTSIDE_MASK_LAYER_ID)) {
    map.addLayer({
      id: MAP_LAB_OUTSIDE_MASK_LAYER_ID,
      type: "fill",
      source: MAP_LAB_OUTSIDE_MASK_SOURCE_ID,
      paint: {
        "fill-color": MAP_LAB_OUTSIDE_MASK_COLOR,
        "fill-opacity": MAP_LAB_OUTSIDE_MASK_OPACITY,
      },
    });
  }

  if (!map.getSource(MAP_LAB_STATE_FILL_SOURCE_ID)) {
    map.addSource(MAP_LAB_STATE_FILL_SOURCE_ID, {
      type: "geojson",
      data: await loadArizonaCountyGeometry(),
    });
  }
  if (!map.getLayer(MAP_LAB_STATE_FILL_LAYER_ID)) {
    map.addLayer({
      id: MAP_LAB_STATE_FILL_LAYER_ID,
      type: "fill",
      source: MAP_LAB_STATE_FILL_SOURCE_ID,
      paint: {
        "fill-color": MAP_LAB_STATE_FILL_COLOR,
        "fill-opacity": 1,
      },
    });
  }
}


export async function addZipLayers(map) {
  ensureMapLabDynamicSource(map);
  if (!map.getSource("arizona-non-zip")) {
    map.addSource("arizona-non-zip", {
      type: "geojson",
      data: await loadArizonaNonZipGeometry(),
    });
  }

  map.addLayer({
    id: "arizona-zip-fills",
    type: "fill",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: zipFilter(),
    layout: { visibility: "none" },
    paint: {
      "fill-color": ["coalesce", ["get", "fillColor"], MAP_LAB_NODATA_COLOR],
      "fill-opacity": ["coalesce", ["get", "fillOpacity"], 0],
    },
  });

  ensureNonZipHatchImage(map);
  map.addLayer({
    id: MAP_LAB_NON_ZIP_LAYER_ID,
    type: "fill",
    source: "arizona-non-zip",
    layout: { visibility: "none" },
    paint: { "fill-pattern": MAP_LAB_NON_ZIP_HATCH_IMAGE },
  });

  map.addLayer({
    id: "arizona-zip-boundaries",
    type: "line",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: zipFilter(),
    layout: { visibility: "none" },
    paint: {
      "line-color": ["coalesce", ["get", "outlineColor"], "#52525b"],
      "line-opacity": ["coalesce", ["get", "outlineOpacity"], 0.48],
      "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.35, 8, 0.9],
    },
  });

  map.addLayer({
    id: "arizona-zip-labels",
    type: "symbol",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: zipFilter(),
    minzoom: 6.4,
    layout: {
      visibility: "none",
      "text-field": ["get", "label"],
      "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 6, 9, 9, 11],
      "text-allow-overlap": false,
      "text-ignore-placement": false,
    },
    paint: {
      "text-color": "#1f2937",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.4,
      "text-halo-blur": 0.4,
    },
  });
}


export async function addCountyLayers(map) {
  ensureMapLabDynamicSource(map);
  map.addLayer({
    id: "arizona-county-fills",
    type: "fill",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: kindFilter(FEATURE_KIND.county),
    layout: { visibility: "none" },
    paint: {
      "fill-color": ["coalesce", ["get", "fillColor"], MAP_LAB_NODATA_COLOR],
      "fill-opacity": ["coalesce", ["get", "fillOpacity"], 0],
    },
  });
  map.addLayer({
    id: "arizona-county-boundaries",
    type: "line",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: kindFilter(FEATURE_KIND.county),
    layout: { visibility: "none" },
    paint: {
      "line-color": ["coalesce", ["get", "outlineColor"], "#3f3f46"],
      "line-opacity": ["coalesce", ["get", "outlineOpacity"], 0.55],
      "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.8, 8, 1.6],
    },
  });
  map.addLayer({
    id: "arizona-county-labels",
    type: "symbol",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: kindFilter(FEATURE_KIND.county),
    layout: {
      visibility: "none",
      "text-field": ["get", "label"],
      "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 5, 10, 8, 14],
      "text-allow-overlap": false,
    },
    paint: {
      "text-color": "#1f2937",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.6,
      "text-halo-blur": 0.4,
    },
  });
}


export function addMapLabDynamicPointLayers(map) {
  const sourceId = MAP_LAB_DYNAMIC_SOURCE_ID;
  ensureMapLabDynamicSource(map, sourceId);
  // Outbreak dots: one GPU symbol layer drawing the baked dot sprite per ZIP. Overlap is
  // allowed (dense = hotspot); bigger dots sort underneath so smaller ones stay visible.
  ensurePieImages(map);
  map.addLayer({
    id: MAP_LAB_PIE_LAYER_ID,
    type: "symbol",
    source: MAP_LAB_DYNAMIC_SOURCE_ID,
    filter: kindFilter(FEATURE_KIND.pie),
    layout: {
      visibility: "none",
      "icon-image": ["coalesce", ["get", "pieIcon"], sickDotImageId()],
      "icon-size": ["coalesce", ["get", "pieScale"], 0.6],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "symbol-sort-key": ["*", -1, ["coalesce", ["get", "sick_count"], 0]],
    },
  });
  addClusterSymbolLayer(map, {
    clusterLayerId: "people-clusters",
    sourceId,
    clusterKind: FEATURE_KIND.personCluster,
    textSize: 11,
  });
  map.addLayer({
    id: "people-cluster-single-points",
    type: "circle",
    source: sourceId,
    filter: singletonPointFilter(FEATURE_KIND.person),
    layout: { visibility: "none" },
    paint: pointCirclePaint(0.85),
  });
  map.addLayer({
    id: "community-report-dot-points",
    type: "circle",
    source: sourceId,
    filter: kindFilter(FEATURE_KIND.person),
    layout: { visibility: "none" },
    paint: pointCirclePaint(0.85),
  });

  addCategoryPointLayers(map, "animals", FEATURE_KIND.animal, FEATURE_KIND.animalCluster, sourceId);
  addCategoryPointLayers(map, "environment", FEATURE_KIND.environment, FEATURE_KIND.environmentCluster, sourceId);
}


function addCategoryPointLayers(map, category, pointKind, clusterKind, sourceId) {
  addClusterSymbolLayer(map, {
    clusterLayerId: `${category}-clusters`,
    sourceId,
    clusterKind,
    textSize: 12,
  });
  map.addLayer({
    id: `${category}-cluster-single-points`,
    type: "circle",
    source: sourceId,
    filter: singletonPointFilter(pointKind),
    layout: { visibility: "none" },
    paint: pointCirclePaint(0.9),
  });
  map.addLayer({
    id: `${category}-points`,
    type: "circle",
    source: sourceId,
    filter: kindFilter(pointKind),
    layout: { visibility: "none" },
    paint: pointCirclePaint(0.9),
  });
}


function addClusterSymbolLayer(map, options) {
  map.addLayer({
    id: options.clusterLayerId,
    type: "symbol",
    source: options.sourceId,
    filter: kindFilter(options.clusterKind),
    layout: {
      visibility: "none",
      "icon-image": ["get", "clusterIconKey"],
      "icon-anchor": "center",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "text-field": ["get", "clusterLabel"],
      "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
      "text-size": options.textSize,
      "text-anchor": "center",
      "text-offset": [0, 0],
      "text-allow-overlap": true,
    },
    paint: { "text-color": "#ffffff" },
  });
}


function pointCirclePaint(fallbackOpacity) {
  return {
    "circle-radius": ["coalesce", ["get", "radius"], 6],
    "circle-color": ["coalesce", ["get", "dotColor"], "#64748b"],
    "circle-opacity": ["coalesce", ["get", "dotOpacity"], fallbackOpacity],
    "circle-stroke-color": "#ffffff",
    "circle-stroke-width": 1.2,
  };
}


export function applyMapLabShadingVisibility() {
  // People are exclusively outbreak dots, so ratio *fills* never render. The boundary
  // lines are the map's background outline so the dots have geographic context: county
  // lines + labels give the bold overview, and the finer ZIP borders sit underneath them
  // to show the actual reporting units. ZIP *labels* stay off (too dense statewide).
  mapInstances.forEach((map) => {
    setLayerVisible(map, "arizona-county-fills", false);
    setLayerVisible(map, "arizona-zip-fills", false);
    setLayerVisible(map, MAP_LAB_NON_ZIP_LAYER_ID, false);
    setLayerVisible(map, "arizona-county-boundaries", true);
    setLayerVisible(map, "arizona-county-labels", true);
    setLayerVisible(map, "arizona-zip-boundaries", true);
    setLayerVisible(map, "arizona-zip-labels", false);
  });
}


export function applyMapLabDrillFilter() {
  const filter = zipFilter();
  mapInstances.forEach((map) => {
    MAP_LAB_ZIP_SHADING_LAYER_IDS.forEach((layerId) => {
      if (map.getLayer(layerId)) map.setFilter(layerId, filter);
    });
  });
}


// Kept as a no-op seam for the current map runtime. County drill-down belonged to the
// removed ratio-shading interaction and cannot change the outbreak-dot view.
export function drillIntoCounty() {
}


export function applyMapLabDotsVisibility() {
  const base = mapLabDotsVisible() && mapLabVisibility().people;
  mapInstances.forEach((map) => {
    // Outbreak pie dots are now the people dot representation.
    setLayerVisible(map, MAP_LAB_PIE_LAYER_ID, base);
    // Retire the legacy sick-rate circle/cluster dot layers (kept defined but never shown).
    MAP_LAB_CLUSTERED_DOT_LAYER_IDS.forEach((layerId) => setLayerVisible(map, layerId, false));
    setLayerVisible(map, "community-report-dot-points", false);
  });
}


export function applyMapLabPinVisibilityToMap(map, category) {
  const on = Boolean(mapLabVisibility()[category]);
  // Animals & environment always render as flat pins — never the clustered layers.
  (CATEGORY_LAYER_IDS[category] || []).forEach((layerId) => {
    const isClusterLayer = layerId.includes("-cluster");
    setLayerVisible(map, layerId, on && !isClusterLayer);
  });
}


export function applyMapLabPinVisibility(category) {
  mapInstances.forEach((map) => applyMapLabPinVisibilityToMap(map, category));
}


export function applyMapLabVisibilityToMap(map) {
  ["animals", "environment"].forEach((category) => applyMapLabPinVisibilityToMap(map, category));
  applyMapLabShadingVisibility();
  applyMapLabDotsVisibility();
  applyMapLabDrillFilter();
}
